<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="500"
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-toolbar color="primary" density="compact">
        <v-toolbar-title>Veldwerk Filters</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialogVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <v-form v-model="valid" @submit.prevent="applyFilters">
          <!-- Date picker -->
          <v-menu
            v-model="datepicker"
            :close-on-content-click="false"
            transition="scale-transition"
            min-width="auto"
            location="bottom"
          >
            <template #activator="{ props }">
              <v-text-field
                v-model="computedDateFormatted"
                label="Datum veldwerkverslag"
                prepend-icon="mdi-calendar"
                variant="outlined"
                density="comfortable"
                class="mb-3"
                clearable
                v-bind="props"
                hint="Formaat: DD-MM-YYYY"
                persistent-hint
                @click:clear="clearDateField"
              ></v-text-field>
            </template>

            <v-date-picker
              v-model="localFilters.date_eq"
              scrollable
              @update:model-value="handleDateSelection"
            >
              <v-btn text color="primary" @click="datepicker = false"
                >Cancel</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="confirmDate">OK</v-btn>
            </v-date-picker>
          </v-menu>

          <!-- User select -->
          <v-select
            v-model="localFilters.user_id_eq"
            :items="users"
            item-title="name"
            item-value="id"
            label="Eigenaar verslag"
            prepend-icon="mdi-account"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            clearable
            @update:model-value="updateSelectedOptions"
          ></v-select>

          <!-- Report type select -->
          <v-select
            v-model="localFilters.day_report_type_id_eq"
            :items="day_report_types"
            item-title="name"
            item-value="id"
            label="Type veldwerkverslag"
            prepend-icon="mdi-format-list-checks"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            clearable
            @update:model-value="updateSelectedOptions"
          ></v-select>

          <!-- Submitted select -->
          <v-select
            v-model="localFilters.submitted_eq"
            :items="submittedOptions"
            item-title="name"
            item-value="value"
            label="Veldwerkverslag afgesloten?"
            prepend-icon="mdi-check-circle-outline"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            clearable
            @update:model-value="updateSelectedOptions"
          ></v-select>

          <v-row class="mt-3">
            <v-col cols="6">
              <v-btn
                block
                color="error"
                variant="outlined"
                prepend-icon="mdi-filter-remove"
                @click="clearForm"
              >
                Wissen
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                color="primary"
                prepend-icon="mdi-filter-check"
                type="submit"
                @click="applyFilters"
              >
                Toepassen
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, reactive, watch } from "vue";
import moment from "moment";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useDayReportTypesStoreRefactor } from "@/stores/dayReportTypesStoreRefactor";

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

// Emits
const emit = defineEmits(["search", "update:modelValue"]);

// Store instances
const usersStore = useUsersStoreRefactor();
const dayReportTypesStore = useDayReportTypesStoreRefactor();

// Computed property for v-model on the dialog
const dialogVisible = computed({
  get: () => props.modelValue.show,
  set: (value) => {
    emit("update:modelValue", {
      ...props.modelValue,
      show: value,
    });
  },
});

// Reactive state
const valid = ref(true);
const datepicker = ref(false);
const selectedOptions = ref(0);

// Local copy of filters to avoid mutating props
const localFilters = reactive({
  date_eq: "",
  user_id_eq: "",
  day_report_type_id_eq: "",
  submitted_eq: "",
  day_report_lines_content_cont: "",
});

// Sync local filters with prop filters
watch(
  () => props.modelValue.q,
  (newVal) => {
    Object.keys(localFilters).forEach((key) => {
      localFilters[key] = newVal[key];
    });
  },
  { deep: true, immediate: true },
);

// Empty filter state for reset
const emptyFilters = {
  date_eq: "",
  user_id_eq: "",
  day_report_type_id_eq: "",
  submitted_eq: "",
  day_report_lines_content_cont: "",
};

// Computed properties
const computedDateFormatted = computed({
  get: () => {
    if (!localFilters.date_eq) return "";
    return moment(localFilters.date_eq).format("DD-MM-YYYY");
  },
  set: (newValue) => {
    try {
      // Allow clearing the field
      if (!newValue) {
        localFilters.date_eq = "";
        return;
      }
      // Parse the date in DD-MM-YYYY format
      const parsedDate = moment(newValue, "DD-MM-YYYY");
      if (parsedDate.isValid()) {
        localFilters.date_eq = parsedDate.format("YYYY-MM-DD");
      }
    } catch (e) {
      console.error("Invalid date format:", e);
    }
  },
});

const users = computed(() => {
  return [{ name: "Alle gebruikers", id: "" }, ...usersStore.activeUsers];
});

const day_report_types = computed(() => {
  return [
    { name: "Alle types", id: "" },
    ...dayReportTypesStore.dayReportTypes,
  ];
});

const submittedOptions = computed(() => {
  return [
    { name: "Alle statussen", value: "" },
    { name: "Ja", value: true },
    { name: "Nee", value: false },
  ];
});

// Methods
function updateSelectedOptions() {
  // Count non-empty values in the filter
  const formValues = Object.values(localFilters).filter(
    (value) => value !== "" && value !== null && value !== undefined,
  );

  // Count the non-empty values
  selectedOptions.value = formValues.length;

  // Ensure date is in the correct format for the API
  if (localFilters.date_eq) {
    try {
      // Convert to ISO format for API
      const date = moment(localFilters.date_eq);
      if (date.isValid()) {
        localFilters.date_eq = date.format("YYYY-MM-DD");
      }
    } catch (e) {
      console.error("Error formatting date:", e);
    }
  }

  // Update parent component with new filter values
  emit("update:modelValue", {
    ...props.modelValue,
    q: { ...localFilters },
    selected_options: selectedOptions.value,
  });
}

function clearDateField() {
  localFilters.date_eq = "";
  updateSelectedOptions();
}

function clearForm() {
  // Reset all filters
  Object.keys(emptyFilters).forEach((key) => {
    localFilters[key] = emptyFilters[key];
  });

  selectedOptions.value = 0;

  // Update parent component with empty filters
  emit("update:modelValue", {
    ...props.modelValue,
    q: { ...emptyFilters },
    selected_options: 0,
    show: false,
  });

  emit("search");
}

function applyFilters() {
  updateSelectedOptions();

  // Close dialog
  emit("update:modelValue", {
    ...props.modelValue,
    show: false,
  });

  emit("search");
}

function handleDateSelection(date) {
  localFilters.date_eq = date;
  updateSelectedOptions();
}

function confirmDate() {
  datepicker.value = false;
  updateSelectedOptions();
}

// Lifecycle hooks
onMounted(() => {
  // Load necessary data for filters
  usersStore.getAll();
  dayReportTypesStore.getAll();
});
</script>
